import { omit } from 'lodash';
import { Col } from 'antd';
import React, { useCallback } from 'react';

import Panel from '.';

import FormItem from '../form-item';
import { DocumentType } from '../../../types/models/document';
import { FormComponentType } from '../../../types/form';
import WithDataSource from '../form-render/with-data-source';
import { getDefaultDataSource } from '../../../utils/common';

export enum Field {
  FileUpload = 0,
  title = 1,
  nameOfTheTnstitution = 2,
  GPAScaleDropdown = 3,
  GPAScaleDetails = 4,
  CumulativeGPA = 5,
  GPAWeightedOrUnweightedDropdown = 6,
  UniversityName = 2,
  EfcAmount = 1,
  InstitutionName = 7,
}

export function getLayoutProps() {
  return { span: 24 };
}

const PanelDocumentUpload = React.memo(function PanelDocumentUpload({
  elements,
  value: panelValue,
  disabled,
  documentType,
  name: panelName,
  ...props
}: any) {
  const renderComponent = useCallback((Comp: React.ComponentType<any>, componentProps: any, error: any) => {
    const layoutProps = getLayoutProps();
    const { label } = componentProps;
    return (
      <Col {...layoutProps}>
        <FormItem label={label} htmlFor={componentProps.id} error={error}>
          <Comp error={error} {...componentProps} />
        </FormItem>
      </Col>
    );
  }, []);

  const renderUniversityAutocomplete = useCallback(
    (
      type: FormComponentType,
      Comp: React.ComponentType<any>,
      componentProps: any,
      error: any,
      documentType: DocumentType
    ) => {
      const layoutProps = getLayoutProps();
      const dataSource = componentProps.dataSource ?? getDefaultDataSource(documentType);
      return (
        <Col {...layoutProps}>
          <FormItem label={componentProps.label} htmlFor={componentProps.id} error={error}>
            <WithDataSource type={type} dataSource={dataSource} {...componentProps}>
              <Comp />
            </WithDataSource>
          </FormItem>
        </Col>
      );
    },
    []
  );

  const renderEnrollmentVerificationComp = useCallback(
    (type: FormComponentType, Comp: React.ComponentType<any>, componentProps: any, error: any, index: number) => {
      const isUniversityName = index === Field.UniversityName;
      if (isUniversityName) {
        return renderUniversityAutocomplete(type, Comp, componentProps, error, DocumentType.ENROLLMENT_VERIFICATION);
      }
      return renderComponent(Comp, componentProps, error);
    },
    [renderUniversityAutocomplete, renderComponent]
  );

  const renderStudentAidReportComp = useCallback(
    (
      Comp: React.ComponentType<any>,
      componentProps: any,
      error: any,
      index: number,
      formatter: Function,
      parser: Function
    ) => {
      const { currencySymbol } = componentProps;
      const isEfcAmount = index === Field.EfcAmount;
      if (isEfcAmount && currencySymbol) {
        componentProps.formatter = formatter;
        componentProps.parser = parser;
      }
      return renderComponent(Comp, componentProps, error);
    },
    [renderComponent]
  );

  const renderTranscriptComp = useCallback(
    (Comp: React.ComponentType<any>, componentProps: any, error: any) => {
      return renderComponent(Comp, componentProps, error);
    },
    [renderComponent]
  );

  const renderFinancialAidAwardLetterComp = useCallback(
    (
      type: FormComponentType,
      Comp: React.ComponentType<any>,
      componentProps: any,
      error: any,
      index: number,
      formatter: Function,
      parser: Function
    ) => {
      const isInstitution = index === Field.InstitutionName;
      if (isInstitution) {
        return renderUniversityAutocomplete(type, Comp, componentProps, error, DocumentType.FINANCIAL_AID_AWARD_LETTER);
      }
      if (type === FormComponentType.Number) {
        componentProps.formatter = formatter;
        componentProps.parser = parser;
      }
      const { currencySymbol } = componentProps;
      if (currencySymbol && type === FormComponentType.Number) {
        componentProps.formatter = formatter;
        componentProps.parser = parser;
      }
      return renderComponent(Comp, componentProps, error);
    },
    [renderUniversityAutocomplete, renderComponent]
  );

  const fieldRenderer = useCallback(
    (type: FormComponentType, attributes: any, error: any, Comp: React.ComponentType<any>, index = 0) => {
      const componentProps = omit(attributes, ['instructiveLabel', 'itemIndex']);
      componentProps.disabled = disabled;

      const isFileUpload = index === Field.FileUpload;
      isFileUpload && (componentProps.documentType = documentType);
      isFileUpload && (componentProps.panelName = panelName);

      const { currencySymbol } = componentProps;
      const formatter = (value: number) => {
        return value ? `${currencySymbol} ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',') : undefined;
      };
      const parser = (value: any) => {
        return value.replace(`${currencySymbol}`, '').trim().replace(/(,*)/g, '');
      };
      switch (documentType) {
        case DocumentType.ENROLLMENT_VERIFICATION: {
          return renderEnrollmentVerificationComp(type, Comp, componentProps, error, index);
        }
        case DocumentType.FINANCIAL_AID_AWARD_LETTER: {
          return renderFinancialAidAwardLetterComp(type, Comp, componentProps, error, index, formatter, parser);
        }
        case DocumentType.STUDENT_AID_REPORT: {
          return renderStudentAidReportComp(Comp, componentProps, error, index, formatter, parser);
        }
        case DocumentType.TRANSCRIPT: {
          return renderTranscriptComp(Comp, componentProps, error, index);
        }
        default:
          return renderComponent(Comp, componentProps, error);
      }
    },
    [
      disabled,
      documentType,
      panelName,
      renderComponent,
      renderEnrollmentVerificationComp,
      renderFinancialAidAwardLetterComp,
      renderStudentAidReportComp,
      renderTranscriptComp,
    ]
  );

  return <Panel {...props} elements={elements} fieldRenderer={fieldRenderer} value={panelValue} disabled={disabled} />;
});

export default PanelDocumentUpload;
